import { Component, ElementRef, Input, OnInit, ViewChild, HostListener, EventEmitter } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DocumentService } from 'src/services/document.service';
import { DocumentItem } from '../document-models/document-item';
import { DocumentComponent } from '../document-models/documentComponent';
import { ReplaySubject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ViewportScroller } from '@angular/common';
import AlphaBarUtils from '../cps-alpha-bar/alpha-bar-utils';
import { Store } from '@ngrx/store';
import { SelectedAlphabar } from '../../actions/alphabet-selection.action';

@Component({
  selector: 'app-cps-resource-document',
  templateUrl: './cps-resource-document.component.html',
  styleUrls: ['./cps-resource-document.component.scss']
})
export class CpsResourceDocumentComponent implements OnInit, DocumentComponent {
  @Input() htmlElement: HTMLElement;
  @ViewChild('docWrapper') documentWrapperElement: ElementRef;
  @ViewChild('alphaBar') alphaBar: ElementRef;

  public doc: DocumentItem;
  public contentLoaded = false;

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();
  public selectedAlphaEvent = new EventEmitter<string>();
  private headingIdPrefix: string;
  public headingIdSufix: string;
  private _buttonIdPrefix = "button_";
  private _scrollOffsetForHeader = 320;
  private alphaBarButtons: HTMLElement[];
  private currentButton: HTMLElement;
  private lastScrollTop = 0;
  private isLoading = true;
  private isClickAction = false;
  private alphaBarElement: HTMLElement | null = null;

  public selectedAlpha = '';
  private isStoreHandled = false;
  private shouldApplyDefaultAlpha = true;

  constructor(
    public translationService: TranslateService,
    private documentService: DocumentService,
    public viewportScroller: ViewportScroller,
    private elementRef: ElementRef,
    private store: Store<any>
  ) { }

  ngOnInit(): void {
    this.checkContentLoaded();
  }

  ngAfterViewChecked() {
    if (this.contentLoaded && this.documentWrapperElement) {
      this.alphaBarElement = this.documentWrapperElement.nativeElement.querySelector('.glossaryAlphaBar');
    }
  }

  checkContentLoaded(): void {
    const thizz = this;
    this.documentService.getContentLoaded()
      .pipe(takeUntil(this.destroy$))
      .subscribe(isLoaded => {
        if (isLoaded) {
          const itemsAlphaBar: HTMLElement[] = Array.from(thizz.htmlElement.querySelectorAll('.contains-alphabar'));
          thizz.alphaBarButtons = Array.from(thizz.htmlElement.querySelectorAll('.call-function-button'));

          const availableAlphas = itemsAlphaBar.map(el => el.getAttribute("value"));
          const headingIdPrefixElement = thizz.htmlElement.querySelector('.heading-id-prefix') as HTMLInputElement;
          const headingIdSufixElement = thizz.htmlElement.querySelector('.heading-id-sufix') as HTMLInputElement;

          if (headingIdPrefixElement && headingIdSufixElement) {
            thizz.headingIdPrefix = headingIdPrefixElement.value;
            thizz.headingIdSufix = headingIdSufixElement.value;
          }

          thizz.unselectAllButtons();

          let isFirstButton = true;
          thizz.alphaBarButtons.forEach(button => {
            const value = button.getAttribute("value");
            button.setAttribute("id", thizz._buttonIdPrefix + value);

            if (!availableAlphas.includes(value)) {
              button.classList.add("glossaryAlphaInactive");
            } else if (isFirstButton && thizz.shouldApplyDefaultAlpha) {
              thizz.selectAlphaBarButton(button);
              thizz.selectedAlpha = value;
              isFirstButton = false;
            }
          });

          thizz.loadFromStore();
        }
        thizz.contentLoaded = isLoaded;
      });
  }

  loadFromStore(): void {
    if (!this.headingIdPrefix || this.isStoreHandled) return;

    this.shouldApplyDefaultAlpha = false; 

    this.store.select(state => state.alphabetSelection)
      .pipe(take(1))
      .subscribe(alphabetState => {
        const storedAlpha = alphabetState?.selectedAlphas?.[this.headingIdPrefix];
        console.log("Loaded from store: " + this.headingIdPrefix + ": " + storedAlpha);

        if (storedAlpha) {
          this.selectedAlpha = storedAlpha;
          this.selectedAlphaEvent.emit(this.selectedAlpha);
          this.selectAlphaBarButtonById(this._buttonIdPrefix + storedAlpha);

          // Delay scrolling until DOM is rendered
          setTimeout(() => {
            this.scrollToAlpha(storedAlpha);
          }, 200);
        } else {
          const firstAvailableButton = this.alphaBarButtons.find(btn =>
            !btn.classList.contains("glossaryAlphaInactive")
          );
          if (firstAvailableButton) {
            const fallbackValue = firstAvailableButton.getAttribute("value");
            this.selectedAlpha = fallbackValue;
            this.selectAlphaBarButton(firstAvailableButton);
            this.store.dispatch(new SelectedAlphabar(this.headingIdPrefix, fallbackValue));

            setTimeout(() => {
              this.scrollToAlpha(fallbackValue);
            }, 200);
          }
        }

        this.isStoreHandled = true;
      });
  }

  scrollToAlpha(letter: string): void {
    const specialLetter = this.specialCharacter(letter);
    const headerId = this.headingIdPrefix + specialLetter + this.headingIdSufix;
    const headerElement = document.querySelector(`[id="${headerId}"]`);

    if (headerElement) {
      const headerPosition = headerElement.getBoundingClientRect().top + document.documentElement.scrollTop;
      const currentDoc = document.querySelector('.active-document');
      let glossaryOffset = currentDoc?.querySelector('.glossaryTopHeadinAlphaBar')?.getBoundingClientRect().height || 0;
      glossaryOffset = AlphaBarUtils.getAdjustedOffsetForGlossaryPages(glossaryOffset);
      const currentScrollOffset = AlphaBarUtils.getScrollOffsetForWindowWidth() + glossaryOffset;

      this.isClickAction = true;
      
      window.scrollTo({
        top: headerPosition - currentScrollOffset,
        behavior: 'smooth'
      });

      setTimeout(() => {
        this.isClickAction = false;
      }, 1000);

    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  @HostListener('document:click', ['$event.target'])
  onclick(targetElement: HTMLElement) {
    if (targetElement.classList.contains('call-function-button')) {
      this.onSelectedAlphaChange(targetElement);
    } else if (targetElement.parentElement?.classList.contains('call-function-button')) {
      this.onSelectedAlphaChange(targetElement.parentElement);
    }
  }

  @HostListener('document:scroll', ['$event.target'])
  onscroll() {
    if (this.contentLoaded && !this.isClickAction) {
      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrolling = currentScrollTop > this.lastScrollTop && !this.isLoading ? 1 : -1;
      this.isLoading = false;
      this.lastScrollTop = Math.max(currentScrollTop, 0);
      this.selectButtonOnScroll(scrolling);
    }
    this.isClickAction = false;
  }

  selectButtonOnScroll(scrolling: number): void {
    if (this.isClickAction || !this.alphaBarElement) return;

    let headerElements: HTMLElement[] = Array.from(
      this.documentWrapperElement.nativeElement.querySelectorAll('.letterHeadingMedGlossary')
    );
    if (headerElements.length === 0) {
      headerElements = Array.from(
        this.documentWrapperElement.nativeElement.querySelectorAll('.letterHeadingMicroOrgGlossary')
      );
    }

    const alphaBarBottom = this.alphaBarElement.getBoundingClientRect().bottom;

    // Reverse list if scrolling up to get nearest header above
    const headersToCheck = scrolling < 0 ? headerElements.slice().reverse() : headerElements;

    for (const el of headersToCheck) {
      const rect = el.getBoundingClientRect();

      const visibleCondition = scrolling < 0
        ? rect.top < alphaBarBottom 
        : rect.top >= alphaBarBottom && rect.top <= window.innerHeight;

      if (visibleCondition) {
        const id = el.getAttribute('id');
        if (!id) return;

        const parts = id.split('_');
        if (parts.length < 3) return;

        const letterPartIndex = parts.length - 2;
        let letter = parts[letterPartIndex];
        letter = letter === '0' ? '#' : (letter === ' ' ? '*' : letter);

        if (this.selectedAlpha !== letter) {
          this.selectedAlpha = letter;
          this.store.dispatch(new SelectedAlphabar(this.headingIdPrefix, letter));
          this.selectAlphaBarButtonById(this._buttonIdPrefix + letter);
        }

        break;
      }
    }
  }

  onSelectedAlphaChange(targetElement: HTMLElement): void {
    let selectedLetter = targetElement.getAttribute("value");
    selectedLetter = this.specialCharacter(selectedLetter);
    this.scrollToAlpha(selectedLetter);
    this.selectAlphaBarButtonById(targetElement.getAttribute("id"));
    this.store.dispatch(new SelectedAlphabar(this.headingIdPrefix, selectedLetter));
    this.isClickAction = true;
  }

  unselectAllButtons(): void {
    this.alphaBarButtons.forEach(button => {
      button.classList.remove("glossaries-alphaSelected");
    });
  }

  selectAlphaBarButtonById(idButton: string): void {
    if (idButton != "button_index"){ 
      const newButton = this.alphaBarButtons.find(e => e.getAttribute("id") == idButton);
      this.selectAlphaBarButton(newButton);
      this.isClickAction = true;
    }
  }

  selectAlphaBarButton(targetElement: HTMLElement): void {
    if (!targetElement || targetElement.getAttribute("id") === this.currentButton?.getAttribute("id")) return;
    this.currentButton?.classList.remove("glossaries-alphaSelected");
    targetElement.classList.add("glossaries-alphaSelected");
    this.currentButton = targetElement;
  }

  specialCharacter(selectedLetter: string): string {
    return selectedLetter === '#' ? "0" : selectedLetter === '*' ? " " : selectedLetter;
  }
}